/**
 * Enhanced Loading States and Visual Feedback Components
 * Comprehensive loading indicators with accessibility and progress states
 */

import React from 'react';
import { Loader2, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import OmButton from './OmButton';

// Loading Spinner with different sizes and styles
export const LoadingSpinner = ({ 
  size = 'md', 
  color = 'blue', 
  className = '',
  label = 'Loading...' 
}) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4', 
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
    '2xl': 'h-12 w-12'
  };

  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    red: 'text-red-600',
    yellow: 'text-yellow-600',
    gray: 'text-gray-600',
    white: 'text-white'
  };

  return (
    <div className={`inline-flex items-center space-x-2 ${className}`} role="status" aria-label={label}>
      <Loader2 className={`animate-spin ${sizeClasses[size]} ${colorClasses[color]}`} />
      <span className="sr-only">{label}</span>
    </div>
  );
};

// Assignment Operation Loading States
export const AssignmentLoadingIndicator = ({ 
  operation, 
  claimId, 
  size = 'sm',
  showText = true,
  inline = false 
}) => {
  const operations = {
    assigning: {
      text: 'Taking claim...',
      icon: Clock,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    releasing: {
      text: 'Releasing claim...',
      icon: Clock,
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-700'
    },
    updating: {
      text: 'Updating claim...',
      icon: Clock,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    }
  };

  const config = operations[operation] || operations.updating;
  const Icon = config.icon;

  if (inline) {
    return (
      <span className="inline-flex items-center space-x-1">
        <LoadingSpinner size={size} color={config.color} />
        {showText && (
          <span className={`text-xs font-medium ${config.textColor}`}>
            {config.text}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className={`inline-flex items-center space-x-2 px-3 py-2 rounded-lg ${config.bgColor}`}>
      <LoadingSpinner size={size} color={config.color} />
      {showText && (
        <span className={`text-sm font-medium ${config.textColor}`}>
          {config.text}
        </span>
      )}
    </div>
  );
};

// Button Loading State
export const LoadingButton = ({ 
  isLoading = false,
  loadingText = 'Loading...',
  children,
  disabled,
  onClick,
  className = '',
  variant = 'primary',
  size = 'sm',
  ...props
}) => {
  const isDisabled = disabled || isLoading;

  // Map variants to OM Design System button types
  const buttonTypeMap = {
    primary: 'filled-button',
    secondary: 'outlined-button', 
    success: 'filled-button',
    danger: 'filled-button',
    warning: 'outlined-button',
    outline: 'outlined-button'
  };

  // Map sizes to OM Design System sizes  
  const sizeMap = {
    xs: 'small',
    sm: 'small', 
    md: 'medium',
    lg: 'large',
    xl: 'large'
  };

  const buttonType = buttonTypeMap[variant] || 'filled-button';
  const buttonSize = sizeMap[size] || 'small';

  return (
    <OmButton
      buttonType={buttonType}
      buttonSize={buttonSize}
      disabled={isDisabled}
      onClick={onClick}
      className={className}
      {...props}
    >
      {isLoading ? (
        <>
          <LoadingSpinner 
            size={size === 'xs' ? 'xs' : size === 'sm' ? 'sm' : 'md'} 
            color="white" 
            className="mr-2" 
          />
          <span>{loadingText}</span>
        </>
      ) : (
        children
      )}
    </OmButton>
  );
};

// Progress Bar Component
export const ProgressBar = ({ 
  progress = 0, 
  showPercent = false,
  color = 'blue',
  size = 'md',
  animated = false,
  label,
  className = ''
}) => {
  const sizeClasses = {
    xs: 'h-1',
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4'
  };

  const colorClasses = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    red: 'bg-red-600',
    yellow: 'bg-yellow-600',
    gray: 'bg-gray-600'
  };

  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          {showPercent && (
            <span className="text-sm text-gray-500">{Math.round(clampedProgress)}%</span>
          )}
        </div>
      )}
      <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${colorClasses[color]} ${sizeClasses[size]} transition-all duration-300 ease-out ${animated ? 'animate-pulse' : ''}`}
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

// Status Badge with Loading States
export const StatusBadge = ({ 
  status, 
  isLoading = false,
  loadingText = 'Updating...',
  size = 'md',
  showIcon = true 
}) => {
  const baseClasses = 'inline-flex items-center font-medium rounded-full';
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const statusConfig = {
    SUBMITTED: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      icon: Clock,
      label: 'Submitted'
    },
    UNDER_REVIEW: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      icon: Clock,
      label: 'Under Review'
    },
    APPROVED: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      icon: CheckCircle,
      label: 'Approved'
    },
    REJECTED: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      icon: XCircle,
      label: 'Rejected'
    },
    MISSING_DOCUMENTS: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      icon: AlertTriangle,
      label: 'Missing Documents'
    }
  };

  const config = statusConfig[status] || {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    icon: Clock,
    label: status
  };

  const Icon = config.icon;

  if (isLoading) {
    return (
      <span className={`${baseClasses} ${sizeClasses[size]} bg-gray-100 text-gray-600`}>
        <LoadingSpinner size="xs" color="gray" className="mr-1" />
        <span>{loadingText}</span>
      </span>
    );
  }

  return (
    <span className={`${baseClasses} ${sizeClasses[size]} ${config.bg} ${config.text}`}>
      {showIcon && <Icon className="h-3 w-3 mr-1" />}
      <span>{config.label}</span>
    </span>
  );
};

// Skeleton Loader for Claims Table
export const ClaimsTableSkeleton = ({ rows = 5 }) => {
  return (
    <div className="bg-crystal-white rounded-lg shadow-sm border border-silver-mist/20 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-silver-mist/30">
          <thead className="bg-deep-jungle">
            <tr>
              <th className="px-6 py-5 text-left">
                <div className="h-4 bg-crystal-white/20 rounded animate-pulse"></div>
              </th>
              <th className="px-6 py-5 text-left">
                <div className="h-4 bg-crystal-white/20 rounded animate-pulse"></div>
              </th>
              <th className="px-6 py-5 text-left">
                <div className="h-4 bg-crystal-white/20 rounded animate-pulse"></div>
              </th>
              <th className="px-6 py-5 text-left">
                <div className="h-4 bg-crystal-white/20 rounded animate-pulse"></div>
              </th>
              <th className="px-6 py-5 text-left">
                <div className="h-4 bg-crystal-white/20 rounded animate-pulse"></div>
              </th>
              <th className="px-6 py-5 text-left">
                <div className="h-4 bg-crystal-white/20 rounded animate-pulse"></div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-silver-mist/10">
            {Array.from({ length: rows }).map((_, index) => (
              <tr key={index} className="animate-pulse">
                <td className="px-6 py-4">
                  <div className="h-4 bg-gray-200 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    <div className="h-8 w-20 bg-gray-200 rounded"></div>
                    <div className="h-8 w-20 bg-gray-200 rounded"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Assignment Action Loading Overlay
export const AssignmentActionOverlay = ({ 
  isVisible = false,
  operation = 'assigning',
  claimId,
  progress = 0 
}) => {
  if (!isVisible) return null;

  const operations = {
    assigning: {
      title: 'Taking Claim',
      description: `Assigning claim #${claimId} to you...`,
      color: 'blue'
    },
    releasing: {
      title: 'Releasing Claim', 
      description: `Releasing claim #${claimId}...`,
      color: 'yellow'
    }
  };

  const config = operations[operation] || operations.assigning;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="loading-title">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
        <div className="text-center">
          <LoadingSpinner size="xl" color={config.color} className="mx-auto mb-4" />
          <h3 id="loading-title" className="text-lg font-semibold text-gray-900 mb-2">
            {config.title}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {config.description}
          </p>
          {progress > 0 && (
            <ProgressBar 
              progress={progress} 
              color={config.color}
              animated={true}
              className="mt-4"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Inline Loading State for Table Rows
export const InlineRowLoading = ({ 
  operation = 'updating',
  className = ''
}) => {
  return (
    <div className={`inline-flex items-center space-x-2 ${className}`}>
      <LoadingSpinner size="xs" color="blue" />
      <span className="text-xs text-gray-600">
        {operation === 'assigning' && 'Taking...'}
        {operation === 'releasing' && 'Releasing...'}
        {operation === 'updating' && 'Updating...'}
      </span>
    </div>
  );
};

export default {
  LoadingSpinner,
  AssignmentLoadingIndicator,
  LoadingButton,
  ProgressBar,
  StatusBadge,
  ClaimsTableSkeleton,
  AssignmentActionOverlay,
  InlineRowLoading
};